import { Button, useTranslation } from '@hbs-us/ui';
import QRCode from 'qrcode';
import { useState } from 'react';

import { ApiError } from '../../lib/apiFetch.js';
import { QcQrPrintCard } from './QcQrPrintCard.js';
import { useQcShareMutation } from './useQc.js';

/**
 * The QR a crew scans on site.
 *
 * It encodes a link to THIS checklist and nothing else. Scanning gets somebody to the right form;
 * signing in is still required, and a photographed code never gets anyone past the door. Whoever
 * scans is added to the crew list, which is what lets a job that picks up an extra pair of hands
 * carry on without a call to the office.
 *
 * Rendered locally to a data URI — no image is fetched, so the code works on a site with a phone
 * hotspot and nothing else, and the link never travels to a third-party generator.
 *
 * PRINTING GOES THROUGH `QcQrPrintCard`, not through this markup. What gets taped to a door frame
 * is a poster, and the on-screen panel is a preview sitting inside an app shell that cannot be
 * printed at all — see that file for why.
 */
export interface QcShareLinkProps {
  checklistId: number;
  /** Printed as the heading, so somebody holding the sheet knows which checklist it opens. */
  checklistName: string;
  /** Job and template line, printed under the heading. */
  subtitle: string;
}

export function QcShareLink({ checklistId, checklistName, subtitle }: QcShareLinkProps) {
  const { t } = useTranslation();
  const share = useQcShareMutation(checklistId);

  const [open, setOpen] = useState(false);
  const [qr, setQr] = useState<string | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [replaced, setReplaced] = useState(false);

  const errorMessage =
    share.error instanceof ApiError
      ? t(`qc:errors.${share.error.message}`, { defaultValue: t('qc:errors.generic') })
      : null;

  const generate = () => {
    share.mutate(undefined, {
      onSuccess: async (response) => {
        if (!response.path) return;
        // The tenant host the browser is already on. Building it here rather than server-side keeps
        // the api out of the business of knowing which host a given crew reaches it by.
        const full = `${window.location.origin}${response.path}`;
        setUrl(full);
        setReplaced(Boolean(response.replacedPreviousLink));
        setQr(
          await QRCode.toDataURL(full, {
            width: 320,
            margin: 1,
            errorCorrectionLevel: 'M',
          }),
        );
        setOpen(true);
      },
    });
  };

  return (
    <section className="mt-4">
      <Button type="button" variant="secondary" disabled={share.isPending} onClick={generate}>
        {t('qc:share.action')}
      </Button>

      {open && qr && url ? (
        <div className="mt-3 rounded-md border border-border bg-card p-4">
          <h3 className="font-semibold text-foreground text-sm">{t('qc:share.heading')}</h3>
          <p className="mt-1 max-w-prose text-muted-foreground text-xs">{t('qc:share.body')}</p>

          {replaced ? (
            // Said plainly: a printed code taped to a door frame has just stopped working.
            <p className="mt-2 font-medium text-warning text-xs">{t('qc:share.replaced')}</p>
          ) : null}

          <img
            src={qr}
            alt={t('qc:share.alt')}
            className="mt-3 h-48 w-48 rounded border border-border bg-white p-2"
          />

          <p className="mt-2 break-all font-mono text-muted-foreground text-xs">{url}</p>

          <div className="mt-3 flex flex-wrap gap-2">
            <Button
              type="button"
              size="sm"
              variant="secondary"
              onClick={() => void navigator.clipboard?.writeText(url)}
            >
              {t('qc:share.copy')}
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => window.print()}>
              {t('qc:share.print')}
            </Button>
            <Button type="button" size="sm" variant="secondary" onClick={() => setOpen(false)}>
              {t('qc:share.hide')}
            </Button>
          </div>

          {/* Mounted only while the panel is open, so `window.print()` from anywhere else in the app
              cannot pick up a stale code for a checklist the user has since navigated away from. */}
          <QcQrPrintCard
            qr={qr}
            url={url}
            checklistName={checklistName}
            subtitle={subtitle}
          />
        </div>
      ) : null}

      {errorMessage ? (
        <p role="alert" className="mt-2 text-destructive text-sm">
          {errorMessage}
        </p>
      ) : null}
    </section>
  );
}
